import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { DataWidgetI, WidgetCache } from '../../dashboard.model';
import { Observable } from 'rxjs';
import { Shortcut } from './shortcut.widget.model';
import * as _ from 'lodash';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';
import { ActionViewPersistenceService } from '../../../Task/ActionView/Action.View.Persistence.Service.upgrade';
import { AdvancedSearchPersistenceService } from '../../../Search/Advanced/advancedSearchPersistence.service';
import { ActionViewService } from '../../../Task/ActionView/action-view-service.upgrade';

@Component({
    selector: 'shortcut-widget',
    template: `
        <h2 class="text-center" style="font-size: 1.5vw; word-break: break-word;">
            @if (!title) {
                <span>Shortcuts</span>
            }
            @if (title) {
                <span>{{ title }}</span>
            }
        </h2>
        <div style="padding: 0 1.5vw; font-size: 1vw;">
            @if (groupByType) {
                <div>
                    @for (group of groupedShortcuts; track group) {
                        <div class="panel-flat-color small-panel">
                            <div class="panel-flat-header small-header" style="height: initial;">
                                <h3 style="font-size: 1.3vw;">{{ group.category }}</h3>
                            </div>
                            <div>
                                @for (shortcut of group.shortcuts; track shortcut) {
                                    <div style="padding-left: 10px;">
                                        <a style="cursor: pointer;" (click)="goToShortcut(shortcut)"
                                            >{{ shortcut.searchName }}
                                            @if (shortcut.variantName) {
                                                <span>- {{ shortcut.variantName }}</span>
                                            }
                                        </a>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
            @if (!groupByType) {
                <div>
                    @for (shortcut of shortcuts; track shortcut) {
                        <div>
                            <a style="cursor: pointer;" (click)="goToShortcut(shortcut)"
                                >{{ shortcut.searchName }}
                                @if (shortcut.variantName) {
                                    <span>- {{ shortcut.variantName }}</span>
                                }
                            </a>
                        </div>
                    }
                </div>
            }
        </div>
    `,
    standalone: false,
})
export class ShortcutWidgetComponent implements OnInit, DataWidgetI {
    constructor(
        private readonly _upgradeNavigationServiceHandler: UpgradeNavigationServiceHandler,
        private readonly _actionViewPersistenceService: ActionViewPersistenceService,
        private readonly _advancedSearchPersistenceService: AdvancedSearchPersistenceService,
        private readonly _actionViewService: ActionViewService,
    ) {}

    @Input() data$: Observable<any>;
    @Output() newParams: EventEmitter<object> = new EventEmitter();

    groupByType: boolean = false;
    shortcuts: Shortcut[];
    groupedShortcuts: { category: string; shortcuts: Shortcut[] }[];
    title: string;

    ngOnInit() {
        this.data$.subscribe((widgetCache: WidgetCache) => {
            this.groupByType = widgetCache.data.groupByType;
            this.title = widgetCache.data.title;

            if (this.groupByType) {
                this.groupedShortcuts = _.chain(widgetCache.data.shortcuts)
                    .groupBy('searchType')
                    .map((shortcuts: Shortcut[], searchType: string) => {
                        return {
                            category: this._getCategory(Number(searchType)),
                            shortcuts: _.sortBy(shortcuts, [(item: Shortcut) => item.searchName.toUpperCase()]),
                        };
                    })
                    .value();
            } else {
                this.shortcuts = _.sortBy(widgetCache.data.shortcuts, [
                    (item: Shortcut) => item.searchName.toUpperCase(),
                ]);
            }
        });
    }

    _getCategory(searchType: number): string {
        switch (searchType) {
            case 0:
                return 'SMART Searches';
            case 1:
                return 'Action Views';
            case 2:
                return 'Reports';
        }
    }

    async goToShortcut(shortcut: Shortcut): Promise<void> {
        let route: string, params: object;

        if (shortcut.searchType === 0) {
            route = 'search';
            params = {
                searchId: shortcut.savedSearchId,
                searchType: shortcut.isSystemSearch ? 0 : 1,
            };

            this._advancedSearchPersistenceService.clearSearchData();
        } else if (shortcut.searchType === 1) {
            shortcut.defaultDisplay = await this._getCurrentActionViewDefaultDisplay(shortcut);

            route = 'actionview';
            params = {
                actionViewId: shortcut.savedSearchId,
                actionViewType: shortcut.isSystemSearch ? 0 : 1,
                displayType: shortcut.defaultDisplay,
            };

            this._actionViewPersistenceService.clearSavedData();
        } else if (shortcut.searchType === 2) {
            route = 'reportmanager';
            params = {
                reportId: shortcut.savedSearchId,
                reportType: shortcut.isSystemSearch ? 0 : 1,
            };
        }

        this._upgradeNavigationServiceHandler.go(route, params);
    }

    private async _getCurrentActionViewDefaultDisplay(shortcut: Shortcut): Promise<number> {
        try {
            const actionView = shortcut.isSystemSearch
                ? await this._actionViewService.getSystemActionViewById(shortcut.savedSearchId)
                : await this._actionViewService.getCustomActionViewById(shortcut.savedSearchId);

            return actionView.defaultDisplay ?? shortcut.defaultDisplay;
        } catch {
            return shortcut.defaultDisplay;
        }
    }
}
